import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/app/StatusBadge";
import { INSP } from "@/constants/testIds";
import { ClipboardCheck, Layers, Download, Building2, Landmark, User } from "lucide-react";
import { PhotoPicker, InspectionEvidence, uploadPhotos } from "@/components/inspection/InspectionEvidence";

const CHECKS = [
  { key: "power_on", label: "Powers on" },
  { key: "screen_ok", label: "Screen OK" },
  { key: "body_ok", label: "Body OK" },
  { key: "keyboard_ok", label: "Keyboard OK" },
  { key: "trackpad_ok", label: "Trackpad OK" },
  { key: "ports_ok", label: "Ports OK" },
  { key: "camera_ok", label: "Camera OK" },
  { key: "battery_ok", label: "Battery OK" },
  { key: "charger_present", label: "Charger present" },
  { key: "accessories_present", label: "Accessories present" },
  { key: "serial_verified", label: "Serial verified" },
  { key: "imei_verified", label: "IMEI verified" },
  { key: "functional_pass", label: "Functional pass" },
];
const NEGATIVE_CHECKS = [
  { key: "physical_damage", label: "Physical damage" },
  { key: "liquid_damage", label: "Liquid damage" },
  { key: "activation_lock", label: "Activation lock" },
  { key: "icloud_active", label: "iCloud active" },
  { key: "mdm_present", label: "MDM present" },
];

export default function Inspection() {
  const [sp] = useSearchParams();
  const presetAsset = sp.get("asset");
  const [awaiting, setAwaiting] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchIds, setBatchIds] = useState([]); // multi-select in batch mode
  const [assetId, setAssetId] = useState("");
  const [grade, setGrade] = useState("A");
  const [notes, setNotes] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [checks, setChecks] = useState(() => {
    const s = {};
    CHECKS.forEach((c) => (s[c.key] = true));
    NEGATIVE_CHECKS.forEach((c) => (s[c.key] = false));
    return s;
  });
  const [recent, setRecent] = useState([]);
  const [pending, setPending] = useState([]); // photos chosen before the record exists
  const [lastSaved, setLastSaved] = useState(null); // open evidence/share panel right after saving

  // Post-recovery inspection candidates: devices that have been recovered / received
  // and are awaiting or undergoing inspection. Warehouse stock is intentionally
  // excluded here (it would bury freshly-received devices) — flip "Show all assets"
  // to inspect any device in any state.
  const INSPECTION_STATES =
    "awaiting_inspection,received,recovered,under_repair,in_transit_recovery,in_transit_reverse";

  const load = async () => {
    const [a, i] = await Promise.all([
      api.get("/assets/", {
        params: showAll
          ? { limit: 200, enrich: 1 }
          : { lifecycle_state: INSPECTION_STATES, limit: 200, enrich: 1 },
      }),
      api.get("/inspections", { params: { limit: 20 } }),
    ]);
    let items = a.data.items || [];
    // Opened from Device 360 with ?asset=<id>: make sure that device is in the list and preselected
    if (presetAsset && !items.some((x) => x.id === presetAsset)) {
      try { const { data } = await api.get(`/assets/${presetAsset}`); if (data?.id) items = [data, ...items]; } catch { /* asset not visible */ }
    }
    setAwaiting(items);
    setRecent(i.data.items || []);
    if (presetAsset && items.some((x) => x.id === presetAsset)) setAssetId((cur) => cur || presetAsset);
  };
  useEffect(() => { load(); }, [showAll, presetAsset]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    // Batch mode with multi-select → POST one inspection per selected asset.
    if (batchMode && batchIds.length > 0) {
      const targets = batchIds.slice();
      let ok = 0, fail = 0;
      for (const aid of targets) {
        try {
          await api.post("/inspections", { asset_id: aid, condition_grade: grade, notes, ...checks });
          ok += 1;
        } catch { fail += 1; }
      }
      if (ok > 0) toast.success(`Stamped ${ok} device${ok === 1 ? "" : "s"} · Grade ${grade}${fail ? ` (${fail} failed)` : ""}`);
      if (ok === 0 && fail > 0) toast.error(`All ${fail} inspections failed`);
      // Auto-advance the single selection to a non-inspected candidate
      setBatchIds([]);
      const remaining = awaiting.filter((a) => !targets.includes(a.id));
      setAssetId(remaining[0]?.id || "");
      load();
      return;
    }
    if (!assetId) {
      toast.error("Select an asset");
      return;
    }
    try {
      const { data: saved } = await api.post("/inspections", { asset_id: assetId, condition_grade: grade, notes, employee_name: employeeName || null, ...checks });
      toast.success("Inspection recorded");
      if (pending.length > 0) {
        const n = await uploadPhotos(saved.id, pending, (done, total) => toast.message(`Uploading photo ${done}/${total}…`, { id: "insp-photo-progress" }));
        toast.success(`${n} photo(s) attached to the inspection report`, { id: "insp-photo-progress" });
        setPending([]);
      }
      try { const { data: fresh } = await api.get(`/inspections/${saved.id}`); setLastSaved(fresh); } catch { setLastSaved(saved); }
      const remaining = awaiting.filter((a) => a.id !== assetId);
      const nextId = remaining.length > 0 ? remaining[0].id : "";
      setAssetId(nextId);
      if (!batchMode) {
        setNotes("");
        setChecks(() => {
          const s = {};
          CHECKS.forEach((c) => (s[c.key] = true));
          NEGATIVE_CHECKS.forEach((c) => (s[c.key] = false));
          return s;
        });
        setGrade("A");
      }
      load();
      if (remaining.length > 0) {
        toast.message(`Next up: ${remaining[0].brand || ""} ${remaining[0].model || ""} · ${remaining[0].serial || ""}`.trim());
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get("/inspections/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      link.setAttribute("download", `SUNTRONIX_ONE_Device_Inspections_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Inspection details exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Selected asset (single-select) → drives the Company / Master / Employee panel.
  const selectedAsset = awaiting.find((a) => a.id === assetId) || null;

  // Default the Employee Name to the recovered-from employee; stays editable.
  useEffect(() => { setEmployeeName(selectedAsset?.employee_name || ""); }, [assetId]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 max-w-[1600px]">
      <div className="lg:col-span-2 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Operations</div>
            <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Device Inspection</h1>
            <p className="text-sm text-slate-500 mt-0.5">Structured post-recovery inspection with condition grading.</p>
          </div>
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={exporting || recent.length === 0}
            data-testid="insp-export-btn"
            className="shrink-0"
            title={recent.length === 0 ? "No inspections to export yet" : "Export inspection details to share with the customer"}
          >
            <Download className="h-4 w-4 mr-1.5" />
            {exporting ? "Exporting…" : "Export"}
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={INSP.form}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs">Asset {showAll ? "(any state)" : "(inspection candidates)"}</Label>
                <button
                  type="button"
                  onClick={() => setShowAll((v) => !v)}
                  className="text-[11px] text-slate-500 hover:text-slate-900 underline decoration-dotted"
                  data-testid="insp-toggle-showall"
                >
                  {showAll ? "Only inspection candidates" : "Show all assets"}
                </button>
              </div>
              {batchMode ? (
                <div className="mt-1 border border-slate-200 rounded-md" data-testid="insp-batch-list">
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 text-[11px] text-slate-500">
                    <span>{batchIds.length} of {awaiting.length} selected</span>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setBatchIds(awaiting.map((a) => a.id))} className="hover:text-slate-900 underline decoration-dotted" data-testid="insp-batch-selectall">Select all</button>
                      <button type="button" onClick={() => setBatchIds([])} className="hover:text-slate-900 underline decoration-dotted" data-testid="insp-batch-clear">Clear</button>
                    </div>
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-slate-100">
                    {awaiting.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-slate-500">No inspection candidates.</div>
                    ) : awaiting.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`insp-batch-row-${a.id}`}>
                        <input
                          type="checkbox"
                          checked={batchIds.includes(a.id)}
                          onChange={() => setBatchIds((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id])}
                        />
                        <div className="text-sm">
                          <div>{a.brand} {a.model}</div>
                          <div className="text-[11px] text-slate-500 font-mono">{a.serial}<span className="ml-2 text-slate-400">{(a.lifecycle_state || "").replace(/_/g, " ")}</span></div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <Select value={assetId} onValueChange={setAssetId}>
                  <SelectTrigger data-testid={INSP.assetSelect} className="h-10 mt-1">
                    <SelectValue placeholder={awaiting.length === 0 ? "No assets found — try Show all" : "Select asset…"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {awaiting.length === 0 ? (
                      <div className="px-3 py-4 text-xs text-slate-500" data-testid="insp-empty">
                        No assets in inspection states. Click <span className="font-medium text-slate-700">Show all assets</span> above to inspect any device.
                      </div>
                    ) : awaiting.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.brand} {a.model} · {a.serial} <span className="text-slate-400">· {(a.lifecycle_state || "").replace(/_/g, " ")}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs">Condition Grade</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger data-testid={INSP.gradeSelect} className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["A", "B", "C", "D", "Scrap"].map((g) => (
                    <SelectItem key={g} value={g}>Grade {g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedAsset && !batchMode && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="insp-org-panel">
              <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50/60">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <Building2 className="h-3.5 w-3.5" /> Company Name
                </div>
                <div className="text-sm text-slate-800 mt-0.5 truncate" data-testid="insp-company-name">
                  {selectedAsset.company_name || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div className="border border-slate-200 rounded-md px-3 py-2 bg-slate-50/60">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <Landmark className="h-3.5 w-3.5" /> Master Company
                </div>
                <div className="text-sm text-slate-800 mt-0.5 truncate" data-testid="insp-master-company">
                  {selectedAsset.master_company_name || <span className="text-slate-400">—</span>}
                </div>
              </div>
              <div className="border border-slate-200 rounded-md px-3 py-2 bg-white">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <User className="h-3.5 w-3.5" /> Employee Name
                </div>
                <input
                  value={employeeName}
                  onChange={(e) => setEmployeeName(e.target.value)}
                  placeholder="Enter employee name"
                  data-testid="insp-employee-name"
                  className="w-full text-sm text-slate-800 mt-0.5 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 placeholder:text-slate-300"
                />
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">Positive Checks</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {CHECKS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">
                  <Checkbox checked={checks[c.key]} onCheckedChange={(v) => setChecks({ ...checks, [c.key]: !!v })} />
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-2">Damage / Lock Flags</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {NEGATIVE_CHECKS.map((c) => (
                <label key={c.key} className="flex items-center gap-2 border border-slate-200 rounded-md px-3 py-2 hover:bg-slate-50">
                  <Checkbox checked={checks[c.key]} onCheckedChange={(v) => setChecks({ ...checks, [c.key]: !!v })} />
                  <span className="text-sm">{c.label}</span>
                </label>
              ))}
            </div>
          </div>
          <PhotoPicker pending={pending} setPending={setPending} />
          <div className="mt-4">
            <Label className="text-xs">Notes</Label>
            <Textarea className="mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observations, defects…" />
          </div>
          <div className="mt-6 flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="insp-batch-toggle-label">
              <input
                type="checkbox"
                checked={batchMode}
                onChange={(e) => setBatchMode(e.target.checked)}
                data-testid="insp-batch-toggle"
              />
              <span>Batch mode — pin checks &amp; grade for the next device</span>
              {batchMode && (
                <span className="ml-1 inline-flex items-center text-[10px] font-semibold uppercase tracking-widest text-[color:var(--st-accent)] bg-[color:var(--st-accent-soft)] rounded-full px-2 py-0.5">
                  On
                </span>
              )}
            </label>
            <Button data-testid={INSP.submitBtn} className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit}>
              {batchMode && batchIds.length > 0 ? (
                <><Layers className="h-4 w-4 mr-1.5" /> Stamp {batchIds.length} devices</>
              ) : (
                <><ClipboardCheck className="h-4 w-4 mr-1.5" /> Save inspection</>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {lastSaved && <div className="bg-white border border-emerald-300 rounded-md p-4" data-testid="insp-last-saved"><div className="flex items-center justify-between"><div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-emerald-700">Saved · Grade {lastSaved.condition_grade} · send proof to the customer</div><button onClick={() => setLastSaved(null)} className="text-[11px] text-slate-500 underline" data-testid="insp-last-saved-close">close</button></div>
          <div className="mt-2"><InspectionEvidence inspection={lastSaved} onChange={() => api.get(`/inspections/${lastSaved.id}`).then(({ data }) => { setLastSaved(data); load(); })} /></div></div>}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Recent inspections</div>
            <span className="text-[10px] text-slate-400">{recent.length}</span>
          </div>
          {recent.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500">None yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr className="text-[10px] uppercase text-slate-500">
                    <th className="px-3 py-2 text-left">S.No</th>
                    <th className="px-3 py-2 text-left">Serial / IMEI</th>
                    <th className="px-3 py-2 text-left">Model</th>
                    <th className="px-3 py-2 text-left">Warranty</th>
                    <th className="px-3 py-2 text-left">Grade</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.slice(0, 12).map((i, idx) => {
                    const a = i.asset || {};
                    const modelParts = [a.brand, a.model].filter(Boolean).join(" ");
                    const serial = a.serial || a.asset_tag || i.asset_id?.slice(0, 8);
                    const imei = a.imei1;
                    // Compute days-remaining for the amber-when-<60-days chip.
                    let daysLeft = null;
                    if (i.warranty_end) {
                      try {
                        const end = new Date(i.warranty_end);
                        const ms = end.getTime() - Date.now();
                        daysLeft = Math.ceil(ms / (1000 * 60 * 60 * 24));
                      } catch { /* ignore */ }
                    }
                    let warrantyLabel = "—";
                    let warrantyChip = "bg-slate-50 text-slate-500 border-slate-100";
                    if (i.warranty_status === "warranty") {
                      if (daysLeft !== null && daysLeft <= 60) {
                        warrantyLabel = `Expiring · ${daysLeft}d`;
                        warrantyChip = "bg-amber-50 text-amber-800 border-amber-200";
                      } else {
                        warrantyLabel = "Warranty";
                        warrantyChip = "bg-emerald-50 text-emerald-800 border-emerald-100";
                      }
                    } else if (i.warranty_status === "non_warranty") {
                      warrantyLabel = "Out of warranty";
                      warrantyChip = "bg-rose-50 text-rose-700 border-rose-100";
                    }
                    return (
                      <tr key={i.id} className="border-t border-slate-100" data-testid={`insp-row-${i.id}`}>
                        <td className="px-3 py-2 font-mono text-slate-500">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-slate-800">{serial}</div>
                          {imei && <div className="font-mono text-[10px] text-slate-500">IMEI {imei}</div>}
                        </td>
                        <td className="px-3 py-2 text-slate-700">{modelParts || <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2 py-0.5 ${warrantyChip}`}>{warrantyLabel}</span>
                        </td>
                        <td className="px-3 py-2"><StatusBadge value={i.condition_grade} /><button onClick={() => setLastSaved(i)} className="mt-1 block text-[10px] text-sky-700 underline" data-testid={`insp-row-evidence-${i.id}`}>{(i.photos || []).length} photo(s) · share</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
